/**
 * `GetStartedSoon` — Project Overview "Snapshot" metrics strip.
 *
 * The component is named `GetStartedSoon` for historical reasons; the visible
 * heading is "Snapshot" per the Figma reference. The file name is preserved
 * to avoid noisy import/path rewrites across the FSD tree — only the rendered
 * copy follows the design.
 *
 * Renders four **purely presentational** stat cards (no hover, no click, no
 * navigation):
 *   1. **Briefs**     — total briefs in the project (via `briefsQueryOptions`).
 *   2. **Artifacts**  — total project artifacts (via `projectArtifactsQuery`).
 *   3. **Assets**     — approved / total uploaded asset versions, formatted
 *                       "approved/total" (e.g. "5/25"), via
 *                       `useProjectApprovalAggregate`.
 *   4. **Comments**   — aggregated `commentsCount` across top-level folders,
 *                       via the same hook. Limitation: does NOT include
 *                       comments on briefs (no project-scoped comments
 *                       endpoint exists; revisit when one ships).
 *
 * All counts default to 0 while loading or on error — same UX as the legacy
 * version, which never showed a spinner here.
 */
import { Badge, type BadgeProps } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import {
  ClockIcon,
  FileTextIcon,
  FolderOpenIcon,
  MessageSquareIcon,
  type LucideIcon,
} from 'lucide-react';
import { briefsQueryOptions } from '@/entities/brief';
import { projectArtifactsQuery } from '@/entities/project-artifact';
import { useProjectApprovalAggregate } from '@/entities/project-folder';
import { useAssetApprovalsHidden } from '@/shared/hooks/useAssetApprovalsHidden';
import { useTranslation } from 'react-i18next';

interface GetStartedSoonProps {
  projectId: string;
  /**
   * Open comments on the project snapshot. Passed in rather than fetched: a
   * second observer on the project detail key re-registers its `queryFn` and
   * would drop the toast suppression the not-found seam sets.
   */
  openComments: number;
}

interface StatCard {
  /** Stable across copy changes, unlike the label the test id used to derive from. */
  id: 'briefs' | 'assets' | 'comments' | 'artifacts';
  icon: LucideIcon;
  label: string;
  /** Rendered as-is — string lets us show formatted values like "5/25". */
  value: string;
  /**
   * Badge tint. Two metrics are call-to-action (approvals waiting, unread
   * comments) and carry a hue; the plain counts stay neutral.
   */
  tone?: 'neutral' | 'waiting' | 'unread';
}

const BADGE_VARIANT = {
  neutral: 'outline',
  waiting: 'warning',
  unread: 'blue',
} as const satisfies Record<NonNullable<StatCard['tone']>, BadgeProps['variant']>;

export function GetStartedSoon(props: GetStartedSoonProps) {
  const { t } = useTranslation('projects');

  // Total briefs (pageSize=1 — we only need `total` from the paginated meta).
  const briefsList = useQuery(
    briefsQueryOptions({ projectId: props.projectId, page: 1, pageSize: 1 }),
  );
  const briefsCount = briefsList.data?.total ?? 0;

  // Total artifacts attached to the project. `take=1` for the same reason.
  const artifactsList = useQuery(projectArtifactsQuery(props.projectId, { skip: 0, take: 1 }));
  const artifactsCount = artifactsList.data?.total ?? 0;

  // Approval + comments aggregate — shares the folders fetch.
  const aggregate = useProjectApprovalAggregate(props.projectId);

  const assetApprovalsHidden = useAssetApprovalsHidden();

  // Icons match the Figma reference:
  //   Briefs    → file-text
  //   Artifacts → sparkles
  //   Assets    → file-text (the document/asset metaphor)
  //   Comments  → message-square-text
  // The design orders the strip so the two metrics that need action sit between
  // the plain counts, and spells each label out in full.
  //
  // The awaiting-approval tile says nothing once approvals are hidden, so it
  // drops out with them.
  const cards: StatCard[] = [
    {
      icon: FileTextIcon,
      id: 'briefs',
      label: t('projectOverview.getStartedSoon.totalBriefs'),
      value: String(briefsCount),
      tone: 'neutral',
    },
    ...(assetApprovalsHidden
      ? []
      : [
          {
            icon: ClockIcon,
            id: 'assets',
            label: t('projectOverview.getStartedSoon.assetsAwaitingApproval'),
            // The label asks how many are waiting, so the count is what is left
            // rather than the approved-of-total ratio.
            value: String(aggregate.totalCount - aggregate.approvedCount),
            tone: 'waiting',
          } satisfies StatCard,
        ]),
    {
      icon: MessageSquareIcon,
      id: 'comments',
      label: t('projectOverview.getStartedSoon.newAssetsComments'),
      value: String(props.openComments),
      tone: 'unread',
    },
    {
      icon: FolderOpenIcon,
      id: 'artifacts',
      label: t('projectOverview.getStartedSoon.totalArtifacts'),
      value: String(artifactsCount),
      tone: 'neutral',
    },
  ];

  return (
    // No wrapping card and no heading: the design stands the four metrics
    // directly on the page frame, so each is its own surface.
    <div className="flex gap-app-inset">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.id}
            className="flex flex-1 flex-col justify-between gap-xl rounded-lg bg-card p-xl"
          >
            {/* Tessra Badge paints icons `text-foreground-subtle`; keep them on the chip
                text colour until agent-studio picks up the upstream Badge icon-colour fix. */}
            <Badge
              variant={BADGE_VARIANT[card.tone ?? 'neutral']}
              roundness="full"
              size="sm"
              className="[&_svg]:text-current"
            >
              <Icon />
              {card.label}
            </Badge>

            <p
              data-testid={`get-started-soon-${card.id}-value`}
              className="self-end text-6xl leading-[56px] font-normal tracking-tight text-foreground"
            >
              {card.value}
            </p>
          </div>
        );
      })}
    </div>
  );
}
